import { createReducer } from '../../../../../../helpers';
import { ReducerAction, ReducerActionWithPayload } from '../../../../../../App.types';
import {
    TiktokAudioStats,
    ClientMonitoredTiktokAudioRelationship,
    TiktokAudioCollection,
    TiktokAudioCollectionHeadlineStats,
    TiktokAudioCollectionPostStats,
    TiktokAudioAsset,
    PaginatedApiResponseData,
} from '@round/api';

export type CollectionsContextState = {
    collectionsLoading: boolean;
    errorLoadingCollections: boolean;
    collections: TiktokAudioCollection[];
    collectionsCount: number;
    hasNextCollectionsPage: boolean;
    collectionsPage: number;
    collectionClientMonitoredAudioRelations: ClientMonitoredTiktokAudioRelationship[];
    collectionClientMonitoredAudioRelationsLoading: boolean;
    selectedCollection: TiktokAudioCollection | undefined;
    headlineStats: TiktokAudioCollectionHeadlineStats | undefined;
    headlineStatsLoading: boolean;
    collectionAudioStats: TiktokAudioStats[];
    collectionAudioStatsCount: number;
    collectionAudioStatsLoading: boolean;
    errorLoadingCollectionAudioStats: boolean;
    audioAssets: TiktokAudioAsset[];
    topPostsStats: TiktokAudioCollectionPostStats[];
    topPostsStatsCount: number;
    topPostsStatsLoading: boolean;
    errorLoadingTopPostsStats: boolean;
};

export const initialState: CollectionsContextState = {
    selectedCollection: undefined,
    collections: [],
    collectionsCount: 0,
    collectionsPage: 1,
    hasNextCollectionsPage: false,
    collectionClientMonitoredAudioRelations: [],
    collectionClientMonitoredAudioRelationsLoading: false,
    collectionsLoading: false,
    errorLoadingCollections: false,
    headlineStats: undefined,
    headlineStatsLoading: false,
    collectionAudioStats: [],
    collectionAudioStatsCount: 0,
    collectionAudioStatsLoading: false,
    errorLoadingCollectionAudioStats: false,
    audioAssets: [],
    topPostsStats: [],
    topPostsStatsCount: 0,
    topPostsStatsLoading: false,
    errorLoadingTopPostsStats: false,
};

export type CollectionsActions =
    | ReducerAction<'loadCollectionOptions'>
    | ReducerAction<'errorLoadingCollectionOptions'>
    | ReducerActionWithPayload<'loadedCollectionOptions', PaginatedApiResponseData<TiktokAudioCollection>>
    | ReducerActionWithPayload<'setHeadlineStatsLoading', boolean>
    | ReducerActionWithPayload<'loadedHeadlineStats', TiktokAudioCollectionHeadlineStats | undefined>
    | ReducerAction<'loadCollectionAudioStats'>
    | ReducerAction<'errorLoadingCollectionAudioStats'>
    | ReducerActionWithPayload<'loadedCollectionAudioStats', PaginatedApiResponseData<TiktokAudioStats>>
    | ReducerActionWithPayload<'loadedAudioAssets', TiktokAudioAsset[]>
    | ReducerAction<'resetAudios'>
    | ReducerActionWithPayload<'setClientMonitoredAudioRelation', ClientMonitoredTiktokAudioRelationship>
    | ReducerAction<'resetTopPosts'>
    | ReducerAction<'loadTopPosts'>
    | ReducerAction<'errorLoadingTopPosts'>
    | ReducerActionWithPayload<'loadedTopPosts', PaginatedApiResponseData<TiktokAudioCollectionPostStats>>
    | ReducerActionWithPayload<'loadMoreCollections', PaginatedApiResponseData<TiktokAudioCollection>>
    | ReducerActionWithPayload<'setSelectedCollection', TiktokAudioCollection | undefined>
    | ReducerAction<'loadClientMonitoredAudioRelations'>
    | ReducerActionWithPayload<'loadedClientMonitoredAudioRelations', ClientMonitoredTiktokAudioRelationship[]>
    | ReducerAction<'errorLoadingClientMonitoredAudioRelations'>
    | ReducerActionWithPayload<'addCollection', TiktokAudioCollection>
    | ReducerActionWithPayload<'updateCollection', TiktokAudioCollection>;

export default createReducer<CollectionsContextState, CollectionsActions>({
    setSelectedCollection: (state, { payload }) => ({
        ...state,
        selectedCollection: payload,
    }),
    loadCollectionOptions: (state) => ({
        ...state,
        collectionsLoading: true,
        errorLoadingCollections: false,
    }),
    errorLoadingCollectionOptions: (state) => ({
        ...state,
        collections: [],
        collectionsLoading: false,
        errorLoadingCollections: true,
    }),
    loadedCollectionOptions: (state, { payload }) => ({
        ...state,
        collectionsLoading: false,
        errorLoadingCollections: false,
        collections: payload.results,
        hasNextCollectionsPage: Boolean(payload.next),
        collectionsCount: payload.count,
    }),
    loadMoreCollections: (state, { payload }) => ({
        ...state,
        collectionsLoading: false,
        errorLoadingCollections: false,
        collections: state.collections.concat(payload.results),
        hasNextCollectionsPage: Boolean(payload.next),
        collectionsCount: payload.count,
        collectionsPage: state.collectionsPage + 1,
    }),
    setHeadlineStatsLoading: (state, { payload }) => ({
        ...state,
        headlineStatsLoading: payload,
    }),
    loadedHeadlineStats: (state, { payload }) => ({
        ...state,
        headlineStats: payload,
    }),
    loadCollectionAudioStats: (state) => ({
        ...state,
        collectionAudioStatsLoading: true,
        errorLoadingCollectionAudioStats: false,
    }),
    errorLoadingCollectionAudioStats: (state) => ({
        ...state,
        audioAssets: [],
        collectionAudioStats: [],
        collectionAudioStatsLoading: false,
        errorLoadingCollectionAudioStats: true,
    }),
    loadedCollectionAudioStats: (state, { payload }) => ({
        ...state,
        collectionAudioStatsLoading: false,
        errorLoadingCollectionAudioStats: false,
        collectionAudioStats: payload.results,
        collectionAudioStatsCount: payload.count,
    }),
    loadedAudioAssets: (state, { payload }) => ({
        ...state,
        audioAssets: payload,
    }),
    resetAudios: (state) => ({
        ...state,
        collectionAudioStats: [],
        collectionAudioStatsCount: 0,
        collectionAudioStatsLoading: false,
        errorLoadingCollectionAudioStats: false,
        audioAssets: [],
    }),
    setClientMonitoredAudioRelation: (state, { payload }) => ({
        ...state,
        collectionClientMonitoredAudioRelations: state.collectionClientMonitoredAudioRelations.map((r) =>
            r.id === payload.id ? payload : r
        ),
    }),
    resetTopPosts: (state) => ({
        ...state,
        topPostsStats: [],
        topPostsStatsLoading: false,
        errorLoadingTopPostsStats: false,
        topPostsStatsCount: 0,
    }),
    errorLoadingTopPosts: (state) => ({
        ...state,
        topPostsStats: [],
        topPostsStatsLoading: false,
        errorLoadingTopPostsStats: true,
    }),
    loadedTopPosts: (state, { payload: { results, count } }) => ({
        ...state,
        topPostsStatsLoading: false,
        topPostsStats: results,
        topPostsStatsCount: count,
    }),
    loadTopPosts: (state) => ({
        ...state,
        errorLoadingTopPostsStats: false,
        topPostsStatsLoading: true,
    }),
    loadClientMonitoredAudioRelations: (state) => ({
        ...state,
        collectionClientMonitoredAudioRelationsLoading: true,
    }),
    errorLoadingClientMonitoredAudioRelations: (state) => ({
        ...state,
        collectionClientMonitoredAudioRelationsLoading: false,
        collectionClientMonitoredAudioRelations: [],
    }),
    loadedClientMonitoredAudioRelations: (state, { payload }) => ({
        ...state,
        collectionClientMonitoredAudioRelations: payload,
        collectionClientMonitoredAudioRelationsLoading: false,
    }),
    addCollection: (state, { payload }) => ({
        ...state,
        collections: state.collections.concat(payload),
        collectionsCount: state.collectionsCount + 1,
    }),
    updateCollection: (state, { payload }) => ({
        ...state,
        collections: state.collections.map((c) => (c.id === payload.id ? payload : c)),
        selectedCollection: state.selectedCollection?.id === payload.id ? payload : state.selectedCollection,
    }),
});
